import React, { useState, useEffect, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaGripLines, FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import Pagination from "../../../../libs/Pagination";
import { addVideo, videoSequence, deleteVideo, getVideos, updateVideo, swapVideoWithPage } from "../../../../libs/api/videoApi";
import { AppContext } from "../../../../context/appContext";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../libs/LoadingSpinner";
import Swal from "sweetalert2";


// Helper function to extract YouTube video ID from various URL formats
const extractYouTubeVideoId = (input) => {
  if (!input) return null;

  // Remove whitespace
  input = input.trim();

  // If it's already just an ID (11 characters, alphanumeric with dash/underscore)
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return input;
  }

  // Pattern 1: youtube.com/watch?v=VIDEO_ID
  let match = input.match(/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];

  // Pattern 2: youtu.be/VIDEO_ID
  match = input.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];

  // Pattern 3: youtube.com/embed/VIDEO_ID
  match = input.match(/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];

  // Pattern 4: m.youtube.com/watch?v=VIDEO_ID
  match = input.match(/(?:m\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];

  return null; // Invalid format
};

// Helper function to fetch video title from YouTube oEmbed API
const fetchYouTubeTitle = async (videoId) => {
  if (!videoId) return null;

  try {
    const response = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
    );

    if (!response.ok) {
      return null; // Video not found or private
    }

    const data = await response.json();
    return data.title || null;
  } catch (error) {
    console.error('Error fetching YouTube title:', error);
    return null;
  }
};

export default function Videos({ currentCategoryId = null }) {
  const { videoCategoriesDropdown, fetchVideoCategoriesDropdown, t, formatNum, isRTL } = useContext(AppContext);
  const [videos, setVideos] = useState([]);
  const [category, setCategory] = useState("");
  const [modalMode, setModalMode] = useState("add");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [youtubeVideoIdInput, setYoutubeVideoIdInput] = useState(""); // User's input (URL or ID)
  const [extractedVideoId, setExtractedVideoId] = useState(""); // Extracted video ID
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [isHidden, setIsHidden] = useState(false);
  const [errors, setErrors] = useState({ title: "", youtubeVideoId: "", category: "" });
  const [isFetchingTitle, setIsFetchingTitle] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loadingAddForm, setLoadingAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  useEffect(() => {
    fetchVideos(true);
  }, [currentPage, limit, debouncedSearch, currentCategoryId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  const fetchVideos = async (isLoading) => {
    setLoading(isLoading);
    try {
      const categoryIdFilter = currentCategoryId === null ? 'null' : currentCategoryId;
      const response = await getVideos(currentPage, limit, debouncedSearch, categoryIdFilter);
      if (response.status === 200) {
        // Add sequence number to each video for drag and drop
        const videosWithSequence = response.data.data.map((video, index) => ({
          ...video,
          sequence: video.sequence || index + 1
        }));
        setVideos(videosWithSequence || []);
        setTotalPages(response.data.totalPages || 1);
        setLimit(response.data.limit || 10);
        setCurrentPage(response.data.currentPage || 1);
        setTotalRecords(response.data.totalRecords || 0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'title':
        if (!value || !value.toString().trim()) {
          error = t('validation.videoTitleRequired');
        }
        break;
      case 'youtubeVideoId':
        if (!value || !value.toString().trim()) {
          error = t('validation.youtubeVideoIdRequired');
        } else {
          // Try to extract video ID
          const extracted = extractYouTubeVideoId(value);
          if (!extracted) {
            error = t('validation.invalidYoutubeUrl');
          }
        }
        break;
      case 'category':
        // Category is now optional, no error needed
        break;
      default:
        break;
    }
    return error;
  };

  const handleFieldChange = async (name, value) => {
    if (name === 'title') setTitle(value);
    if (name === 'description') setDescription(value);
    if (name === 'youtubeVideoId') {
      setYoutubeVideoIdInput(value);
      // Extract video ID in real-time
      const extracted = extractYouTubeVideoId(value);
      setExtractedVideoId(extracted || '');

      // Auto-fetch title if we have a valid video ID and title is empty
      if (extracted && !title) {
        setIsFetchingTitle(true);
        setTitle(t('common.loading'));
        const fetchedTitle = await fetchYouTubeTitle(extracted);
        if (fetchedTitle) {
          setTitle(fetchedTitle);
        } else {
          setTitle(''); // Reset if fetch failed
        }
        setIsFetchingTitle(false);
      }
    }
    if (name === 'category') setCategory(value);
    // Validate field
    const error = validateField(name, value);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    newErrors.title = validateField('title', title);
    newErrors.youtubeVideoId = validateField('youtubeVideoId', youtubeVideoIdInput);
    setErrors(newErrors);

    // If there are errors, do not proceed
    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    // Use the extracted video ID, not the user's input
    const payload = {
      title,
      youtubeVideoId: extractedVideoId,
      isHidden: isHidden,
    };

    if (description) {
      payload.description = description;
    }

    if (category) {
      payload.categoryId = category;
    }

    try {
      setLoadingAddForm(true);
      const response = await addVideo(payload);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("video_modal").close();
        toast.success(response?.data?.message || t('messages.videoAddedSuccess'));
        fetchVideos(false);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  };

  const handleUpdate = async () => {
    const newErrors = {};
    newErrors.title = validateField('title', title);
    newErrors.youtubeVideoId = validateField('youtubeVideoId', youtubeVideoIdInput);
    setErrors(newErrors);

    // If there are errors, do not proceed
    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    // Use the extracted video ID, not the user's input
    const payload = {
      title,
      youtubeVideoId: extractedVideoId,
      isHidden: isHidden,
    };

    if (description) {
      payload.description = description;
    }

    if (category) {
      payload.categoryId = category;
    }

    try {
      setLoadingAddForm(true);
      const response = await updateVideo(selectedVideoId, payload);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("video_modal").close();
        toast.success(response?.data?.message || t('messages.videoUpdatedSuccess'));
        fetchVideos(false);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  }

  const handleDelete = async (id) => {
    // Show confirmation dialog using SweetAlert
    const result = await Swal.fire({
      title: t('dialogs.areYouSure'),
      text: t('dialogs.cantRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('dialogs.yesDelete'),
      cancelButtonText: t('dialogs.cancel')
    });

    if (!result.isConfirmed) {
      return; // User cancelled the deletion
    }

    try {
      const response = await deleteVideo(id);
      if (response.status === 200 || response.status === 201) {
        toast.success(response?.data?.message || t('messages.videoDeletedSuccess'));
        fetchVideos(false);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  }

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const openModal = (mode, video = null) => {
    // Refresh video categories dropdown to get latest categories
    fetchVideoCategoriesDropdown();

    setModalMode(mode);
    setSelectedVideoId(null);
    if (mode === "edit" && video) {
      setTitle(video?.title || "");
      setDescription(video?.description || "");
      setYoutubeVideoIdInput(video?.youtubeVideoId || "");
      setExtractedVideoId(video?.youtubeVideoId || "");
      setSelectedVideoId(video?._id || null);
      setCategory(video?.categoryId?._id || "");
      setIsHidden(video?.isHidden || false);
      setErrors({ title: "", youtubeVideoId: "", category: "" });
    } else {
      setTitle("");
      setDescription("");
      setYoutubeVideoIdInput("");
      setExtractedVideoId("");
      setSelectedVideoId(null);
      setCategory("");
      setIsHidden(false);
      setErrors({ title: "", youtubeVideoId: "", category: "" });
    }

    document.getElementById("video_modal").showModal();
  };

  const clearForm = () => {
    setTitle("");
    setDescription("");
    setYoutubeVideoIdInput("");
    setExtractedVideoId("");
    setSelectedVideoId(null);
    setCategory("");
    setIsHidden(false);
    setErrors({ title: "", youtubeVideoId: "", category: "" });
  }

  const handleDragStart = (e, index) => {
    setDraggedItem(videos[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    // Create a custom drag image that only shows the dragged item
    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);

    // Style the drag image to look like a single item
    dragImage.style.width = rect.width + 'px';
    dragImage.style.height = rect.height + 'px';
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    dragImage.style.backgroundColor = 'white';
    dragImage.style.border = '2px solid #0D6EFD';
    dragImage.style.borderRadius = '8px';
    dragImage.style.zIndex = '9999';

    // Hide the original element during drag
    draggedElement.style.opacity = '0.3';

    // Set the custom drag image
    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(videos[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = videos[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedVideos = videos.map(video => {
      if (video._id === draggedItem._id) {
        return { ...video, sequence: targetSequence };
      }
      if (video._id === draggedOverItem._id) {
        return { ...video, sequence: draggedSequence };
      }
      return video;
    });

    // Sort by sequence to reflect the swap
    updatedVideos.sort((a, b) => a.sequence - b.sequence);

    setVideos(updatedVideos);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);

    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedUpdateSequence'));
      // Revert if update fails
      fetchVideos(false);
    }
  };

  const handleDragEnd = (e) => {
    // Restore the original element's opacity
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence
      }
      const response = await videoSequence(payload);
      if (response.status === 200 || response.status === 201) {
        fetchVideos(false);
      }
    } catch (error) {
      toast.error(error?.data?.message || t('messages.somethingWentWrong'));
    }
  }

  const handleSwapWithPage = async (videoId, direction) => {
    try {
      const payload = {
        id: videoId,
        direction: direction,
        currentPage: currentPage,
        limit: limit
      };
      const response = await swapVideoWithPage(payload);
      if (response.status === 200) {
        toast.success(response.data.message || t('messages.videoMovedSuccessfully'));
        fetchVideos(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">{t('video.title')}</h2>

      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Video */}
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('forms.searchPlaceholder')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2  border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('video.addVideo')}
          </button>
        </div>

        {/* Table */}
        {loading && <div className="flex justify-center items-center h-full p-6"><LoadingSpinner /></div>}
        {!loading && videos.length === 0 && (
          <div className="flex justify-center items-center h-full p-6">
            <p className="text-gray-500">{t('video.noVideos')}</p>
          </div>
        )}
        {!loading && videos.length > 0 && (
          <div className="w-full">
            {/* Table Header */}
            <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-6 border-b border-[#F7F7F9]">
              <div className={`p-2 py-4 ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('common.id')}</div>
              <div className="p-2 py-4">{t('video.thumbnail')}</div>
              <div className="p-2 py-4">{t('video.videoTitle')}</div>
              <div className="p-2 py-4">{t('video.youtubeVideoId')}</div>
              <div className="p-2 py-4">{t('forms.category')}</div>
              <div className="p-2 py-4">{t('common.actions')}</div>
            </div>

            {/* Draggable Content */}
            <div>
              {videos.map((video, index) => (
                <div
                  key={video._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`grid grid-cols-6 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none ${
                    draggedItem?._id === video._id ? "opacity-50 bg-gray-100 shadow-lg" : ""
                  } ${
                    dragOverItem?._id === video._id ? "border-t-2 border-blue-500 bg-blue-50" : ""
                  }`}
                  style={{
                    cursor: 'move',
                    transition: 'all 0.3s ease',
                    userSelect: 'none'
                  }}
                >
                  <div className={`p-2 py-6 ${isRTL ? 'pr-8' : 'pl-8'} flex items-center gap-2`}>
                    <FaGripLines className="text-gray-400" />
                    {formatNum((currentPage - 1) * limit + index + 1)}
                  </div>
                  <div className="p-2 py-6">
                    {video?.youtubeVideoId ? (
                      <img
                        src={getYouTubeThumbnail(video.youtubeVideoId)}
                        alt={video.title}
                        className="w-20 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-20 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-400">
                        N/A
                      </div>
                    )}
                  </div>
                  <div className="p-2 py-6 flex items-center gap-2">
                    <span>{video?.title || "-"}</span>
                    {video?.isHidden && (<span className="badge badge-sm bg-gray-200 text-gray-600 border-0">{t('forms.hidden')}</span>)}
                  </div>
                  <div className="p-2 py-6">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono">
                      {video?.youtubeVideoId || "-"}
                    </span>
                  </div>
                  <div className="p-2 py-6">{video?.categoryId?.title || "-"}</div>
                  <div className="p-2 py-6">
                    <div className="flex gap-3 items-center">
                      {index === 0 && currentPage > 1 && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(video._id, 'previous');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToPreviousPage')}
                        >
                          <FaArrowLeft size={18} />
                        </button>
                      )}
                      {index === limit - 1 && currentPage < totalPages && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(video._id, 'next');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToNextPage')}
                        >
                          <FaArrowRight size={18} />
                        </button>
                      )}
                      <BiEditAlt
                        size={24}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => openModal("edit", video)}
                      />
                      <FaRegTrashCan
                        size={20}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => handleDelete(video._id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && videos.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((currentPage - 1) * limit + 1)} {t('pagination.to')} {formatNum(Math.min(currentPage * limit, totalRecords))} {t('pagination.of')} {formatNum(totalRecords)} {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <dialog
        id="video_modal"
        className="modal shadow-[0px_1px_2px_0px_#0A0D120F]"
      >
        {" "}
        <div className="modal-box min-w-[800px] rounded-2xl p-0">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {modalMode === "add" ? t('video.addVideo') : t('video.editVideo')}
              </h3>
              <form method="dialog">
                <button className="text-xl hover:text-red-500">
                  <IoClose />
                </button>
              </form>
            </div>

            {/* Title */}
            <label className="block text-sm font-medium mb-2">{t('video.videoTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder={t('video.enterVideoTitle')}
              className=" w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.title && <p className="text-red-500 absolute -mt-[15px] text-xs">{errors.title}</p>}

            {/* YouTube Video ID or URL */}
            <label className="block text-sm font-medium mb-2 mt-4">{t('video.youtubeVideoIdOrUrl')}</label>
            <input
              type="text"
              value={youtubeVideoIdInput}
              onChange={(e) => handleFieldChange('youtubeVideoId', e.target.value)}
              placeholder={t('video.youtubeUrlPlaceholder')}
              className=" w-full mb-2 border rounded-xl p-3 border-[#808080]"
              dir="ltr"
            />
            {errors.youtubeVideoId && <p className="text-red-500 text-xs mb-2">{errors.youtubeVideoId}</p>}
            {extractedVideoId && !errors.youtubeVideoId && (
              <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <p className={`text-xs text-green-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                  ✓ {t('video.videoIdExtracted')}: <span className="font-mono font-semibold">{extractedVideoId}</span>
                </p>
              </div>
            )}
            <p className={`text-xs text-gray-500 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('video.youtubeUrlHint')}
            </p>

            {/* Description */}
            <label className="block text-sm font-medium mb-2 mt-4">{t('video.descriptionOptional')}</label>
            <textarea
              value={description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder={t('video.enterVideoDescription')}
              className=" w-full mb-4 border rounded-xl p-3 border-[#808080]"
              rows="4"
              dir={isRTL ? 'rtl' : 'ltr'}
            />

            {/* Category */}
            <label className="block text-sm font-medium mb-2 mt-4">
              {t('video.videoCategoryOptional')}
            </label>
            <select
              value={category}
              onChange={(e) => handleFieldChange('category', e.target.value)}
              className=" w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t('video.selectCategoryOptional')}</option>
              {videoCategoriesDropdown?.data?.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.displayTitle || cat.title}
                </option>
              ))}
            </select>
            {errors.category && <p className="text-red-500 absolute -mt-[15px] text-xs">{errors.category}</p>}

            {/* Hidden */}
            <div className="flex items-center gap-2 mt-8">
              <input
                type="checkbox"
                id="video_isHidden"
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                className="checkbox checkbox-sm"
              />
              <label htmlFor="video_isHidden" className="text-sm font-medium cursor-pointer">{t('forms.hidden')}</label>
            </div>

            {/* YouTube Preview */}
            {extractedVideoId && (
              <div className="mt-6">
                <label className="block text-sm font-medium mb-2">{t('video.preview')}</label>
                <div className="border rounded-xl overflow-hidden">
                  <img
                    src={getYouTubeThumbnail(extractedVideoId)}
                    alt="YouTube Thumbnail"
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="modal-action bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
            <button
              type="button"
              disabled={loadingAddForm || loading}
              onClick={modalMode === "add" ? handleSubmit : handleUpdate}
              className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
            >
              {loadingAddForm ? <LoadingSpinner /> : modalMode === "add" ? t('common.add') : t('common.update')}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
